import * as React from 'react';
import {
  Box,
  UtilityText,
  InputStatusMessage,
  Spinner,
  StackLayout,
} from '@leagueplatform/genesis-core';
import {
  DomainEntity,
  getEntityInstanceName,
  findEntityModulePath,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { HintText } from '@web-config-app/core-react-ui';
import {
  AddItemModalTriggers,
  AddItemByIdModal,
  AddEntityReferenceFromListModal,
  PrimitiveControlFormFieldBanner,
} from '@web-config-app/entity-form';
import type {
  EntityFormControlProps,
  AddItemModalTrigger,
} from '@web-config-app/entity-form';
import {
  useConfigApp,
  useConfigAppParams,
  useConfigAppPath,
} from '@web-config-app/core-react';
import { EntitySelectValue } from './entity-select-value.component';
import { EntitySelectModalHeading } from './entity-select-modal-heading.component';

/**
 * Separate out the pure UI component from the functionality so that it's possible to show it in storybook
 */

interface EntitySelectControlPresenterProps
  extends Pick<
    EntityFormControlProps,
    'label' | 'hint' | 'inputStatus' | 'banner' | 'enabled' | 'statusMessage'
  > {
  isLoading: boolean;
  isError: boolean;
  entityReferenceData?: any;
  isAddingById?: boolean;
  isAddingFromList: boolean;
  isLoadingByIdSubmission?: boolean;
  isLoadingByIdError?: boolean;
  isMissingRelationshipError?: boolean;
  openChooseFromList: () => void;
  closeChooseFromList: () => void;
  closeAddById?: () => void;
  addItemTriggerList: AddItemModalTrigger[];
  domainEntity: DomainEntity;
  onSubmitAddById?: (args: any) => void;
  onSubmitChooseFromList: (args: any) => void;
  onClearEntityReference: (args: any) => void;
  idValue?: string;
  parentEntityId?: string;
  setIdValue?: React.Dispatch<React.SetStateAction<string>>;
  isRelationshipReference: boolean;
  domainEntityAttributesQuery?: string | null;
  isArrayItem?: boolean;
}

export const EntitySelectControlPresenter: React.FC<
  EntitySelectControlPresenterProps
> = ({
  label,
  hint,
  statusMessage,
  inputStatus,
  banner,
  entityReferenceData,
  isLoading,
  isError,
  enabled,
  isLoadingByIdSubmission = false,
  isLoadingByIdError,
  isMissingRelationshipError,
  addItemTriggerList,
  domainEntity,
  onSubmitAddById,
  onSubmitChooseFromList,
  onClearEntityReference,
  isAddingById = false,
  isAddingFromList,
  closeAddById,
  closeChooseFromList,
  openChooseFromList,
  idValue,
  setIdValue,
  parentEntityId,
  isArrayItem = false,
  isRelationshipReference,
  domainEntityAttributesQuery,
}) => {
  const { formatMessage } = useIntl();
  const { modules = [], entityConfigs } = useConfigApp();
  const { environmentKey } = useConfigAppParams();
  const generateConfigPaths = useConfigAppPath();
  const entityModulePath = findEntityModulePath(
    modules,
    domainEntity.type,
    entityConfigs,
  );
  const { getEntityDetailsPathForId } = generateConfigPaths({
    tabId: entityModulePath?.tabId,
    moduleId: entityModulePath?.moduleId,
    childModuleId: entityModulePath?.childModuleId,
    environmentKey,
  });
  const showAddById = onSubmitAddById && closeAddById && setIdValue;

  return (
    <>
      <PrimitiveControlFormFieldBanner banner={banner}>
        <Box css={{ width: '100%' }}>
          <UtilityText
            emphasis="subtle"
            css={{ typography: '$label', marginBlockEnd: '$quarter' }}
          >
            {label}
          </UtilityText>
          {hint && (
            <HintText css={{ paddingBlockEnd: '$quarter' }}>{hint}</HintText>
          )}

          <div aria-live="polite">
            {!entityReferenceData && (
              <StackLayout css={{ width: '100%' }} horizontalAlignment="center">
                <Spinner loading={isLoading} />
              </StackLayout>
            )}
            {entityReferenceData ? (
              <EntitySelectValue
                title={getEntityInstanceName(entityReferenceData, domainEntity)}
                href={getEntityDetailsPathForId(entityReferenceData.id)}
                openChooseFromList={openChooseFromList}
                isArrayItem={isArrayItem}
                disabled={!enabled}
                secondaryText={
                  entityReferenceData?.attributes?.entityMetadata
                    ?.domainEntityId
                }
                status={entityReferenceData?.attributes?.entityMetadata?.status}
                onClear={() => onClearEntityReference(entityReferenceData)}
                copyItemLabel={formatMessage({ id: 'ENTITY_ID' })}
              />
            ) : null}
            {isError && (
              <InputStatusMessage inputStatus="error">
                {formatMessage(
                  { id: 'ERROR_LOADING_ENTITY_REFERENCE' },
                  {
                    hasValue: entityReferenceData ? 'true' : 'false',
                    value: entityReferenceData,
                  },
                )}
              </InputStatusMessage>
            )}
            {isMissingRelationshipError && (
              <InputStatusMessage inputStatus="error">
                {formatMessage(
                  { id: 'MISSING_RELATIONSHIP' },
                  { entityId: entityReferenceData?.id },
                )}
              </InputStatusMessage>
            )}
          </div>

          {!entityReferenceData && (
            <AddItemModalTriggers
              triggerList={addItemTriggerList}
              disabled={!enabled}
            />
          )}
          {inputStatus && (
            <InputStatusMessage inputStatus={inputStatus}>
              {statusMessage}
            </InputStatusMessage>
          )}
        </Box>
      </PrimitiveControlFormFieldBanner>
      {showAddById && (
        <AddItemByIdModal
          headingLabel={
            <EntitySelectModalHeading domainEntity={domainEntity} />
          }
          open={isAddingById}
          fieldLabel={formatMessage({ id: 'ENTITY_ID' })}
          errorMessage={
            isLoadingByIdError
              ? formatMessage(
                  { id: 'UNABLE_TO_FIND_ENTITY' },
                  {
                    entityName: formatMessage(
                      { id: domainEntity.labelKey },
                      { count: 1 },
                    ),
                  },
                )
              : undefined
          }
          onCancel={() => closeAddById()}
          onSubmit={onSubmitAddById}
          idValue={idValue}
          setIdValue={setIdValue}
          isLoadingSubmission={isLoadingByIdSubmission}
        />
      )}
      {domainEntity && (
        <AddEntityReferenceFromListModal
          headingLabel={
            <EntitySelectModalHeading domainEntity={domainEntity} />
          }
          domainEntityAttributesQuery={domainEntityAttributesQuery}
          open={isAddingFromList}
          onCancel={() => closeChooseFromList()}
          domainEntity={domainEntity}
          parentEntityId={parentEntityId}
          onSubmit={onSubmitChooseFromList}
          isRelationshipReference={isRelationshipReference}
        />
      )}
    </>
  );
};
